<script setup lang="ts">
import { ref, defineProps,onMounted } from "vue";
import { getPassKeyStatus,Credential, getRegistrationChallenge, registerWebAuthn, getAuthenticationChallenge, verifyWebAuthn } from "@/services/Auth";
import { FingerPrintIcon } from '@heroicons/vue/24/solid';
import {getData, setData, NewSparkData} from '@/services/idb'
import { useBase64UrlDecoder } from '@/services/base64'; // Adjust the import path as needed


const props = defineProps<{
	userId: number;
	actionType: 'authenticate' | 'register'; // Define the action type as a prop
}>();


const isProcessing = ref(false);
const errorMessage = ref("");
const enabledFaceID = ref(false);
const { arrayBufferToBase64, base64ToArrayBuffer,base64UrlDecode, arrayBufferToBase64URL, base64UrlEncode } = useBase64UrlDecoder();
const checkPassKey = async () => {
	try {
		const response = await getPassKeyStatus(props.userId);
		console.log(response);
	} catch (error) {
		console.log(error);
	}
};
const registerFaceId = async () => {
	isProcessing.value = true;

	try {
		// **Step 1: Get Challenge from Server**
		const response = await getRegistrationChallenge(props.userId);
		const challenge = response.AuthenticationQueries.getRegistrationChallenge.challenge;
		const attestationOptions = {
			challenge: challenge, // The challenge you obtain from your server
			rp: { name: 'New Spark Media Platform' },
			user: {
				id: `${props.userId}`, // Convert userId to string directly
				name: 'userName',
				displayName: 'User Display Name',
			},
			pubKeyCredParams: [
				{ 
					type: 'public-key',  // Explicitly set type to the literal 'public-key'
					alg: -7, // ES256 (RSA is -257)
				},
			] as PublicKeyCredentialParameters[], // Explicitly cast as PublicKeyCredentialParameters[]
			timeout: 60000,
			attestation: 'direct' as 'direct', // Correctly assign one of the allowed values
		};



		const credential = await navigator.credentials.create({
			publicKey: {
				...challenge,
				// Decode the challenge and user ID
				challenge: base64UrlDecode(challenge.challenge),
				user: {
					...challenge.user,
					id: base64UrlDecode(challenge.user.id),
				},
			},
		}) as PublicKeyCredential;
		if (!credential) {
			throw new Error('Failed to create credentials');
		}
		const attestationResponse = credential.response as AuthenticatorAttestationResponse;

		// **Step 3: Send WebAuthn Response to Server**
		const registerResponse = await registerWebAuthn(
			props.userId,{'clientDataJSON':base64UrlEncode(credential.response.clientDataJSON),'attestationObject': base64UrlEncode(attestationResponse.attestationObject)}
		);
		if(registerResponse.AuthenticationMutations.registerWebAuthn.status=="true") {
			await setData({dbId:props.userId,faceId:1});
			enabledFaceID.value=true;

		}

	} catch (error) {
		console.log(error);
	} finally {
		isProcessing.value = false;
	}
};
const authenticateFaceId = async () => {
	isProcessing.value = true;
	errorMessage.value = "";

	try {
		// **Step 1: Get Challenge from Server**
		const response = await getAuthenticationChallenge();
		const data = response.AuthenticationQueries.getAuthenticationChallenge;
		if (!data.publicKey) {
			return;
		}
		const challenge = base64ToArrayBuffer(data.publicKey.challenge);
		const allowCredentials = data.publicKey.allowCredentials.map((cred: Credential) => ({
			id: base64ToArrayBuffer(cred.id), // Convert credential ID
			type: cred.type, // Ensure this is 'public-key'
			transports: cred.transports || ['usb', 'ble', 'nfc', 'internal'], // Default transports
		}));
		const publicKeyCredentialRequestOptions = {
			challenge,
			rpId: data.publicKey.rpId,
			allowCredentials,
			userVerification: data.publicKey.userVerification || 'preferred', // Default to 'preferred'
			timeout: data.publicKey.timeout || 60000, // Default timeout
		};
const credential = await navigator.credentials.get({ publicKey: publicKeyCredentialRequestOptions })
    .then((cred) => {
        if (cred) {
            // Cast `cred` to `PublicKeyCredential`
            const publicKeyCredential = cred as PublicKeyCredential;

            // Cast `response` to `AuthenticatorAssertionResponse`
            const assertionResponse = publicKeyCredential.response as AuthenticatorAssertionResponse;

            // Ensure the response is valid
            if (assertionResponse.authenticatorData && assertionResponse.clientDataJSON && assertionResponse.signature) {
                const credentialForRequest = {
                    id: publicKeyCredential.id,
                    rawId: arrayBufferToBase64(publicKeyCredential.rawId),
                    type: publicKeyCredential.type,
                    authenticatorAttachment: publicKeyCredential.authenticatorAttachment,
                    response: {
                        authenticatorData: arrayBufferToBase64(assertionResponse.authenticatorData),
                        clientDataJSON: arrayBufferToBase64(assertionResponse.clientDataJSON),
                        signature: arrayBufferToBase64(assertionResponse.signature),
                        userHandle: assertionResponse.userHandle ? arrayBufferToBase64(assertionResponse.userHandle) : null,
                    },
                };
                return credentialForRequest;
            } else {
                throw new Error('Invalid WebAuthn response');
            }
        } else {
            throw new Error('No credential returned');
        }
    })
    .catch((error) => {
        console.error('Error:', error);
        throw error; // Re-throw the error to handle it elsewhere
    });
		const authResponse =await verifyWebAuthn(props.userId,credential);
		if(authResponse.AuthenticationMutations.verifyWebAuthn.status=='true') {
			window.location.href = "/";
		} else {
			console.log(authResponse);
		}
	} catch (error) {
		console.error('Authentication error:', error);
		errorMessage.value = 'An error occurred during authentication.';
	} finally {
		isProcessing.value = false;
	}
};

onMounted(async () => {
	const storedData = await getData();
	await checkPassKey();
	if(storedData && storedData.faceId==1) {
		enabledFaceID.value=true;
	}
});

</script>

<template>
	<FingerPrintIcon v-if="props.actionType=='register'" @click.prevent="registerFaceId()" class="h-full w-full text-primary" :class="{'text-secondary' : !enabledFaceID}"/>
	<FingerPrintIcon v-else-if="enabledFaceID" @click.prevent="authenticateFaceId()" class="h-12 w-12 text-primary"/>
</template>

<style scoped>
.face-id-wrapper {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 20px;
	border: 1px solid #ccc;
	border-radius: 8px;
	background: white;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

button {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 10px 20px;
	font-size: 16px;
	cursor: pointer;
	background: #007bff;
	color: white;
	border: none;
	border-radius: 5px;
	transition: background 0.3s;
}

button:disabled {
	background: #ccc;
	cursor: not-allowed;
}

.error {
	color: red;
	margin-top: 10px;
}
</style>

